/**
 * plugins/list-item.ts — Item de lista genérico
 *
 * Cobre: .steps, .flow, .flow-sub, .sec-list, .listas, .action-card
 * Ferramentas: duplicar abaixo, mover ↑↓, excluir, renumeração auto em .steps.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, directChildOf, moveSibling, renumberNums, closestInside } from '../types';

const LIST_SELECTORS = ['.steps', '.flow', '.flow-sub', '.sec-list', '.listas', '.flow-box .steps', '.env-req-box .list'];

export class ListItemPlugin implements VtbPlugin {
    readonly panelId = 'vtb-list-item';
    readonly selectorHints = ['.steps > li', '.flow > li', '.flow-sub > li', '.sec-list > li', '.listas > li', '.action-card'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        // Busca container de lista
        const container = closestInside(target, visualEditor, e => {
            if (e.dataset.vtbId === 'steps' || e.dataset.vtbId === 'flow' || e.dataset.vtbId === 'list') return true;
            return LIST_SELECTORS.some(sel => e.matches ? e.matches(sel) : e.classList.contains(sel.replace('.', '')));
        });

        if (container) {
            const item = directChildOf(container, target);
            if (item && (item.tagName === 'LI' || item.dataset.vtbId === 'li')) {
                return { 
                    type: this.panelId, 
                    label: 'Item de Lista', 
                    el: item, 
                    container,
                    extra: { listType: container.dataset.vtbId || container.className } 
                };
            }
        }

        // action-card (especial)
        const card = closestInside(target, visualEditor, e => 
            e.classList.contains('action-card') || e.dataset.vtbId === 'card'
        );
        if (card) {
            return { 
                type: this.panelId, 
                label: 'Card', 
                el: card, 
                container: card.parentElement ?? undefined,
                extra: { listType: 'card' } 
            };
        }

        return null;
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const lbl = registry.q<HTMLElement>('vtb-list-item-label');
        if (lbl) {
            lbl.textContent = ctx.extra?.listType === 'action-card' ? '🃏 Card' : '📋 Item';
        }
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-item-add')?.addEventListener('click',  () => this._addBelow(registry));
        registry.q('vtb-item-up')?.addEventListener('click',   () => this._move(registry, -1));
        registry.q('vtb-item-down')?.addEventListener('click', () => this._move(registry,  1));
        registry.q('vtb-item-del')?.addEventListener('click',  () => this._delete(registry));
    }

    // ── Operações ─────────────────────────────────────────────────────────────

    private _addBelow(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const clone = ctx.el.cloneNode(true) as HTMLElement;
        clone.classList.remove('vtb-selected');
        if (ctx.el.nextSibling) ctx.el.parentElement!.insertBefore(clone, ctx.el.nextSibling);
        else ctx.el.parentElement!.appendChild(clone);
        if (ctx.container) renumberNums(ctx.container);
        registry.sync();
        registry.selectEl(clone);
    }

    private _move(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        moveSibling(ctx.el, dir);
        if (ctx.container) renumberNums(ctx.container);
        registry.sync();
    }

    private _delete(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const container = ctx.container;
        ctx.el.remove();
        if (container) renumberNums(container);
        registry.clearSelection();
        registry.sync();
    }
}
